/**
 * PrivilegePopup — Modale d'accueil pour promouvoir une offre "featured".
 * - Charge GET /api/privileges/featured-popup au mount
 * - Skip si aucun offer (204)
 * - Cookie itercatus_privilege_dismissed_{offer_id} valable 4h : ne pas ré-afficher
 * - Affiche après 3s pour ne pas être agressif
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFeaturedPrivilegePopup, getMedia } from "../lib/api";
import { getPublicMediaUrl } from "./../lib/mediaUrl";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent } from "./ui/dialog";
import { Sparkles, Lock, Calendar, X, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const DISPLAY_DELAY_MS = 3000;
const STORAGE_KEY = "itercatus_privilege_dismissed"; // sessionStorage: auto-efface a la fermeture du navigateur

function readDismissedIds() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function writeDismissedIds(map) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {}
}

function isDismissed(offerId) {
  const map = readDismissedIds();
  return !!map[offerId]; // pas de TTL : session-only
}

function markDismissed(offerId) {
  const map = readDismissedIds();
  map[offerId] = true;
  writeDismissedIds(map);
}

export default function PrivilegePopup() {
  const { isMember } = useAuth();
  const [offer, setOffer] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    getFeaturedPrivilegePopup()
      .then((res) => {
        if (cancelled || res.status !== 200 || !res.data) return;
        const o = res.data;
        if (isDismissed(o.id)) return;
        setOffer(o);
        if (o.main_image_id) {
          getMedia(o.main_image_id).then((r) => {
            if (!cancelled) setImageUrl(getPublicMediaUrl(r.data));
          }).catch(() => {});
        }
        const t = setTimeout(() => { if (!cancelled) setOpen(true); }, DISPLAY_DELAY_MS);
        return () => clearTimeout(t);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleClose = () => {
    if (offer?.id) markDismissed(offer.id);
    setOpen(false);
  };

  const handleDiscover = () => {
    if (offer?.id) markDismissed(offer.id);
    setOpen(false);
    if (offer?.id) navigate(`/privileges/${offer.id}`);
  };

  if (!offer) return null;
  const endDate = offer.end_date ? format(new Date(offer.end_date), "dd MMMM yyyy", { locale: fr }) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden border-0 gap-0"
        data-testid="privilege-popup"
      >
        {/* Image de teaser */}
        <div className="relative aspect-[16/10] bg-secondary">
          {imageUrl ? (
            <img src={imageUrl} alt={offer.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Sparkles className="h-16 w-16 opacity-20 text-primary" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 bg-white/95 hover:bg-white rounded-full p-2 shadow-md transition-colors"
            data-testid="privilege-popup-close"
            aria-label="Fermer"
          >
            <X className="h-4 w-4 text-primary" />
          </button>
          <Badge className="absolute top-3 left-3 bg-primary/95 text-white border-0 backdrop-blur-sm">
            <Sparkles className="h-3 w-3 mr-1" />
            Escale Privilège
          </Badge>
        </div>

        {/* Contenu */}
        <div className="p-6 space-y-4">
          <div>
            <h2 className="font-heading text-2xl font-bold text-primary mb-2">{offer.title}</h2>
            <p className="text-sm text-muted-foreground">{offer.hook}</p>
          </div>

          {isMember && !offer.is_teaser ? (
            <>
              {offer.member_price != null && offer.public_price != null && offer.savings_percent > 0 && (
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-primary">{offer.member_price.toFixed(2)} €</span>
                  <span className="text-sm text-muted-foreground line-through">{offer.public_price.toFixed(2)} €</span>
                  <Badge className="bg-green-100 text-green-700 border-green-200 ml-auto">
                    -{offer.savings_percent}%
                  </Badge>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 text-sm text-primary/80 bg-primary/5 rounded-lg px-3 py-2">
              <Lock className="h-4 w-4" /> Offre réservée aux adhérents
            </div>
          )}

          {endDate && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Valable jusqu'au {endDate}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button variant="ghost" onClick={handleClose} className="sm:w-auto" data-testid="privilege-popup-dismiss">
              Plus tard
            </Button>
            <Button onClick={handleDiscover} className="flex-1" data-testid="privilege-popup-discover">
              Découvrir l'offre <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
